import type { ReceiptAudit } from '../services/auditService';
import type { AuditSortKey } from '../types/features/auditListing';
import {
  resolveNonTicketReportDate,
  resolveNonTicketReportTime,
} from '../features/audit/fixOcr/utils/nonTicketReportDateUtils';

const parseIsoDate = (value: string): { year: number; month: number; day: number } | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const [, yearRaw, monthRaw, dayRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!year || !month || !day) {
    return null;
  }

  return { year, month: month - 1, day };
};

const parseTimePart = (value: string): { hours: number; minutes: number; seconds: number } | null => {
  const match = value.match(/\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/);
  if (!match) {
    return null;
  }

  const [, hoursRaw, minutesRaw, secondsRaw] = match;
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsRaw || 0);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return null;
  }

  return { hours, minutes, seconds };
};

const resolveReportType = (audit: ReceiptAudit): string => {
  const finalJson = (audit as any)?.parsed_data?.final_json;
  return String((audit as any)?.report_type || finalJson?.report_type || '').toLowerCase();
};

export const getReceiptAuditShiftStartMs = (audit: ReceiptAudit): number => {
  try {
    const finalJson = (audit as any)?.parsed_data?.final_json ?? {};
    const reportType = resolveReportType(audit);
    const parsedDate = parseIsoDate(resolveNonTicketReportDate(reportType, finalJson, audit));
    const parsedTime = parseTimePart(resolveNonTicketReportTime(reportType, finalJson, audit));

    if (parsedDate) {
      const date = new Date(
        parsedDate.year,
        parsedDate.month,
        parsedDate.day,
        parsedTime?.hours ?? 0,
        parsedTime?.minutes ?? 0,
        parsedTime?.seconds ?? 0,
      );
      const ms = date.getTime();
      if (!Number.isNaN(ms)) {
        return ms;
      }
    }

    const shiftDateTime = Date.parse(String((audit as any)?.shift_date_time || ''));
    if (!Number.isNaN(shiftDateTime)) {
      return shiftDateTime;
    }

    const createdAtMs = Date.parse(audit.created_at);
    return Number.isNaN(createdAtMs) ? 0 : createdAtMs;
  } catch {
    const createdAtMs = Date.parse(audit.created_at);
    return Number.isNaN(createdAtMs) ? 0 : createdAtMs;
  }
};

export const getReceiptAuditCreatedAtMs = (audit: ReceiptAudit): number => {
  const createdAtMs = Date.parse(audit.created_at);
  return Number.isNaN(createdAtMs) ? 0 : createdAtMs;
};

export const getReceiptAuditProcessedAtMs = (audit: ReceiptAudit): number => {
  const processedAtMs = Date.parse(String((audit as any)?.updated_at || ''));
  return Number.isNaN(processedAtMs) ? 0 : processedAtMs;
};

export const sortReceiptAudits = (
  audits: ReceiptAudit[],
  orderBy: AuditSortKey,
  order: 'asc' | 'desc',
): ReceiptAudit[] => {
  const sortableAudits = [...audits];

  const getSortValue = (audit: ReceiptAudit): number => {
    switch (orderBy) {
      case 'createdAt':
        return getReceiptAuditCreatedAtMs(audit);
      case 'processedAt':
        return getReceiptAuditProcessedAtMs(audit);
      case 'shiftDateTime':
      default:
        return getReceiptAuditShiftStartMs(audit);
    }
  };

  sortableAudits.sort((left, right) => {
    const leftValue = getSortValue(left);
    const rightValue = getSortValue(right);

    if (leftValue < rightValue) {
      return order === 'asc' ? -1 : 1;
    }

    if (leftValue > rightValue) {
      return order === 'asc' ? 1 : -1;
    }

    const leftCreatedAt = getReceiptAuditCreatedAtMs(left);
    const rightCreatedAt = getReceiptAuditCreatedAtMs(right);

    if (leftCreatedAt < rightCreatedAt) {
      return order === 'asc' ? -1 : 1;
    }

    if (leftCreatedAt > rightCreatedAt) {
      return order === 'asc' ? 1 : -1;
    }

    return 0;
  });

  return sortableAudits;
};
